/**
 * @file 「選んで聞く」の質問欄である (リーダーの下に出る細い1段)。
 *
 * @what: 左に選んだもの (押すと外せる)、中に「AI 解説」「AI 採点」、右に聞くことと補足することの
 *   自由入力を置く。補足することは AI の答えには混ぜず、記録に別に残る (本人が述べたこと)。
 *   AI のボタンを押すと backend に依頼し、返ってきた答えをその場でモーダルに出す。答えは1件の記録に
 *   なり、評価と削除もそのモーダルからできる。上限に達したら足せないことを伝える。
 *   「AI 採点」は選んだものに本人の解答を含みうるもの (メモ由来か、書き込みのある紙面) が含まれる
 *   ときだけ出す。呼び出し側が autoAsk を渡すと、開いた直後にそのボタンを押したのと同じ動きをする
 *   (「ページに書き込む」の「このページを AI 採点」が使う)。
 * @why: 対象と問いを分け、選んだら画面の下に欄が出る、という形で合意している (代表、2026-09-08)。
 *   ダイアログにしないのは、選び直しながら書けることを優先するためである。モデルは backend が呼ぶ
 *   (web から直接呼ぶとキーが端末に出る)。
 * @failure: 依頼に失敗したら1行で伝え、選んだものはそのまま残す (押し直せる)。
 */

import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/shared/api/client";
import {
  closeAskRecord,
  deleteAskRecord,
  fetchAskRecord,
  postAskTurnReaction,
  streamAskRecord,
  type AIReactionReason,
  type StudyAskRecord,
} from "../api/askRecords";
import { AskRecordDetail } from "../guides/AskRecordPanel";
import { ASK_TARGETS_MAX, canGrade, type AskTarget } from "../model/askSelection";
import { AskStreamView } from "./AskStreamView";
import { StudyDialog } from "./StudyDialog";
import { SparkIcon } from "./readerIcons";
import "./study.css";

/**
 * 聞けなかったときに出す1行を選ぶ。
 *
 * サーバが理由を返したときはそれを出す (回数の上限・採点なのにメモが無い、など本人が直せるもの)。
 * それ以外は内部の詳しい話を見せず、押し直せることだけを伝える。
 */
function askErrorText(err: unknown): string {
  if (err instanceof ApiError && (err.status === 400 || err.status === 429)) {
    const body = err.body as { message?: unknown } | undefined;
    if (body && typeof body.message === "string" && body.message !== "") return body.message;
  }
  return "AI に聞けませんでした。時間を置いて、もう一度押してください。";
}

export function AskBar({
  familyId,
  learnerPersonId,
  targets,
  question,
  onQuestionChange,
  note,
  onNoteChange,
  onRemove,
  onClear,
  onAsked,
  autoAsk,
  onAutoAskDone,
}: {
  familyId: string;
  /** 聞く人 (この人の記録として残る)。決まっていなければ AI のボタンを押せない。 */
  learnerPersonId: string | null;
  targets: readonly AskTarget[];
  /** 聞くこと (対象を選び直しても消えないよう、持ち主は呼び出し側)。 */
  question: string;
  onQuestionChange: (value: string) => void;
  /** 補足すること (自信のほど・どこまでできたか・答え合わせの結果など)。AI の答えとは別に残る。 */
  note: string;
  onNoteChange: (value: string) => void;
  onRemove: (ref: string) => void;
  onClear: () => void;
  /** 聞けたときに呼ぶ (ページの「AI 解説」の件数を数え直すため)。 */
  onAsked?: (() => void) | undefined;
  /** 渡されたら、開いた直後にそのボタンを押したのと同じに聞く。受け取ったら onAutoAskDone で知らせる。 */
  autoAsk?: { preset: "ai_explain" | "ai_grade" } | null | undefined;
  onAutoAskDone?: (() => void) | undefined;
}) {
  const [asking, setAsking] = useState<"ai_explain" | "ai_grade" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answer, setAnswer] = useState<StudyAskRecord | null>(null);
  // 作っている途中の答えである (届いた分をそのまま出す)。
  const [partial, setPartial] = useState<string | null>(null);
  // 途中で閉じられたら、できあがっても開き直さない (記録は残るので件数から読める)。
  const closedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);
  const targetKey = targets.map((t) => t.ref).join(" ");
  // 選んだものや書いたものが変わったら、前の失敗の文言は消す。
  useEffect(() => {
    setError(null);
  }, [targetKey, question, note]);

  const ask = async (preset: "ai_explain" | "ai_grade") => {
    if (learnerPersonId === null || asking !== null) return;
    setAsking(preset);
    setError(null);
    setPartial("");
    closedRef.current = false;
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const record = await streamAskRecord(
        familyId,
        learnerPersonId,
        {
          refs: targets.map((t) => t.ref),
          preset_id: preset,
          ...(question.trim() === "" ? {} : { question: question.trim() }),
          ...(note.trim() === "" ? {} : { self_report: note.trim() }),
        },
        (chunk) => setPartial((got) => (got ?? "") + chunk),
        controller.signal,
      );
      setPartial(null);
      if (!closedRef.current) setAnswer(record);
      onAsked?.();
    } catch (err: unknown) {
      setPartial(null);
      if (controller.signal.aborted) return;
      console.error("[study] failed to ask", err);
      setError(askErrorText(err));
    } finally {
      setAsking(null);
    }
  };

  // 呼び出し側からの「すぐ聞く」は1回だけ受け、受けたことを返して二度と押されないようにする。
  useEffect(() => {
    if (!autoAsk) return;
    onAutoAskDone?.();
    void ask(autoAsk.preset);
    // ask はそのときの選んだもので聞く。autoAsk が来たときだけ動かす。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAsk]);

  const reload = async () => {
    if (answer === null || learnerPersonId === null) return;
    setAnswer(await fetchAskRecord(familyId, learnerPersonId, answer.id));
  };

  // 答えを開いている間は、選んだものを外しても欄ごと消さない (読んでいる途中で閉じないため)。
  if (targets.length === 0 && answer === null) return null;
  const busy = asking !== null;

  return (
    <div className="study-askbar" role="group" aria-label="選んだものについて聞く">
      <ul className="study-askbar-targets">
        {targets.map((t) => (
          <li key={t.ref}>
            <button
              type="button"
              className="study-askbar-chip"
              aria-label={`${t.label} を選ぶのをやめる`}
              onClick={() => onRemove(t.ref)}
            >
              {t.label}
              <span aria-hidden="true">×</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="study-askbar-actions">
        <button
          type="button"
          className="study-viewer-btn study-viewer-btn--small study-viewer-btn--ai"
          disabled={busy || learnerPersonId === null}
          title={learnerPersonId === null ? "まなぶ人を選ぶと聞けます" : undefined}
          onClick={() => void ask("ai_explain")}
        >
          <SparkIcon />
          {asking === "ai_explain" ? "聞いています..." : "AI 解説"}
        </button>
        {canGrade(targets) ? (
          <button
            type="button"
            className="study-viewer-btn study-viewer-btn--small study-viewer-btn--ai"
            disabled={busy || learnerPersonId === null}
            title={learnerPersonId === null ? "まなぶ人を選ぶと聞けます" : undefined}
            onClick={() => void ask("ai_grade")}
          >
            <SparkIcon />
            {asking === "ai_grade" ? "聞いています..." : "AI 採点"}
          </button>
        ) : null}
        <button
          type="button"
          className="study-viewer-btn study-viewer-btn--small"
          disabled={busy}
          onClick={onClear}
        >
          選ぶのをやめる
        </button>
      </div>
      <input
        type="text"
        className="study-askbar-input"
        aria-label="聞くことを書く"
        placeholder="聞くこと (任意)"
        value={question}
        maxLength={500}
        onChange={(e) => onQuestionChange(e.target.value)}
      />
      <input
        type="text"
        className="study-askbar-input"
        aria-label="補足することを書く"
        placeholder="補足すること (任意)"
        value={note}
        maxLength={500}
        onChange={(e) => onNoteChange(e.target.value)}
      />
      {error !== null ? (
        <p className="study-askbar-hint" role="alert">
          {error}
        </p>
      ) : null}
      {targets.length >= ASK_TARGETS_MAX ? (
        <p className="study-askbar-hint">
          選べるのは{ASK_TARGETS_MAX}つまでです。ほかを選ぶには、どれかを外してください。
        </p>
      ) : null}
      {partial !== null ? (
        <StudyDialog
          title={asking === "ai_grade" ? "AI 採点" : "AI 解説"}
          labelledBy="study-askbar-partial-title"
          onClose={() => {
            closedRef.current = true;
            setPartial(null);
          }}
          size="wide"
        >
          <AskStreamView text={partial} />
        </StudyDialog>
      ) : null}
      {answer !== null && learnerPersonId !== null ? (
        <StudyDialog
          title={answer.turns[0]?.preset_id === "ai_grade" ? "AI 採点" : "AI 解説"}
          labelledBy="study-askbar-answer-title"
          onClose={() => setAnswer(null)}
          size="wide"
        >
          <AskRecordDetail
            record={answer}
            familyId={familyId}
            onReact={async (
              turnIndex: number,
              body: { helpful: boolean; reasons?: AIReactionReason[]; comment?: string },
            ) => {
              await postAskTurnReaction(familyId, learnerPersonId, answer.id, turnIndex, body);
              await reload();
            }}
            onReload={reload}
            onClose={async () => {
              setAnswer(await closeAskRecord(familyId, learnerPersonId, answer.id));
            }}
            onDelete={async () => {
              await deleteAskRecord(familyId, learnerPersonId, answer.id);
              setAnswer(null);
            }}
          />
        </StudyDialog>
      ) : null}
    </div>
  );
}
